import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import ViewTestCaseModal from '../test-suites/ViewTestCaseModal';
import {
  Breadcrumbs,
  ErrorState,
  LoadingState,
  PageScaffold,
  PriorityBadge,
  StatusBadge,
} from '../ui';

const mapCase = (tc, projectId) => ({
  id: tc.test_case_id || tc.id,
  name: tc.name,
  status: tc.status,
  priority: tc?.priority?.toLowerCase?.() || tc?.priority,
  suiteId: tc.suite_id,
  suiteName: tc.suite_name,
  project_id: projectId || tc.project_id,
  description: tc.description,
  automation_status: tc.automation_status,
  execution_time: tc.execution_time,
  environment: tc.environment,
  environment_id: tc.environment_id,
  browser: tc.browser,
  device: tc.device,
  tags: tc.tags,
  created_by: tc.created_by,
  created_at: tc.created_at,
  updated_at: tc.updated_at,
  ai_generated: tc.ai_generated,
  is_healed: tc.is_healed,
  steps: tc.steps || {
    step_description: [],
    expected_result: [],
    prerequisites: [],
    additional_notes: [],
  },
  test_suites: tc.test_suites,
});

/**
 * Standalone Test Case detail — preserves list pagination via ?ret=
 * and suite origin via ?from=suite&suite_id=.
 */
const TestCaseDetailPage = () => {
  const { testCaseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const projectId =
    searchParams.get('project_id') ||
    getProjectIdFromURL() ||
    selectedProject?.id;

  const fromSuite = searchParams.get('from') === 'suite';
  const suiteIdParam = searchParams.get('suite_id');
  const suiteNameParam = searchParams.get('suite_name');

  const [testCase, setTestCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const originSuiteId = suiteIdParam || testCase?.suiteId || null;
  const originSuiteName =
    (suiteNameParam ? decodeURIComponent(suiteNameParam) : null) ||
    testCase?.suiteName ||
    (originSuiteId ? `Suite ${originSuiteId}` : null);

  const cameFromSuite = fromSuite && originSuiteId;

  const goToSuite = useCallback(() => {
    if (!originSuiteId) return;
    const workspaceId = getWorkspaceIdFromURL();
    const pid = getProjectIdFromURL() || projectId;
    const path = `test-suites/${originSuiteId}?project_id=${pid || ''}`;
    if (workspaceId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, pid, path));
    } else {
      navigate(`/dashboard/${path}`);
    }
  }, [originSuiteId, getWorkspaceIdFromURL, getProjectIdFromURL, projectId, navigate]);

  const goToCasesList = useCallback(() => {
    const workspaceId = getWorkspaceIdFromURL();
    const pid = getProjectIdFromURL() || projectId;
    const ret = searchParams.get('ret');
    let listQuery = '';
    if (ret) {
      try {
        listQuery = `?${decodeURIComponent(ret)}`;
      } catch {
        listQuery = `?${ret}`;
      }
    }

    if (workspaceId) {
      navigate(`${buildWorkspaceDashboardPath(workspaceId, pid, 'test-cases')}${listQuery}`);
    } else {
      navigate(`/dashboard/test-cases${listQuery}`);
    }
  }, [getWorkspaceIdFromURL, getProjectIdFromURL, projectId, navigate, searchParams]);

  /** Primary back — suite when opened from suite, otherwise cases list */
  const goBack = useCallback(() => {
    if (cameFromSuite) {
      goToSuite();
      return;
    }
    goToCasesList();
  }, [cameFromSuite, goToSuite, goToCasesList]);

  const fetchCase = useCallback(async () => {
    if (!testCaseId || !projectId) {
      setError('Missing test case or project context.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/test_case/cases_list', {
        params: {
          project_id: projectId,
          test_case_id: testCaseId,
          page: 1,
          page_size: 10,
        },
      });
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      let found = list.find(
        (tc) => String(tc.test_case_id || tc.id) === String(testCaseId)
      );

      if (!found) {
        const res2 = await axiosInstance.get('/test_case/cases_list', {
          params: {
            project_id: projectId,
            search: String(testCaseId),
            page: 1,
            page_size: 50,
          },
        });
        const list2 = Array.isArray(res2?.data) ? res2.data : [];
        found = list2.find(
          (tc) => String(tc.test_case_id || tc.id) === String(testCaseId)
        );
      }

      if (!found) {
        setError('Test case not found in this project.');
        setTestCase(null);
      } else {
        setTestCase(mapCase(found, projectId));
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load test case.');
    } finally {
      setLoading(false);
    }
  }, [testCaseId, projectId]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const handleCrumbNavigate = useCallback(
    (path) => {
      if (path === 'suite' || path === 'test-suites') {
        if (path === 'suite' && originSuiteId) {
          goToSuite();
        } else {
          const workspaceId = getWorkspaceIdFromURL();
          const pid = getProjectIdFromURL() || projectId;
          if (workspaceId) {
            navigate(buildWorkspaceDashboardPath(workspaceId, pid, 'test-suites'));
          } else {
            navigate('/dashboard/test-suites');
          }
        }
        return;
      }
      goToCasesList();
    },
    [originSuiteId, goToSuite, goToCasesList, getWorkspaceIdFromURL, getProjectIdFromURL, projectId, navigate]
  );

  const crumbs = useMemo(() => {
    const items = cameFromSuite
      ? [
          { label: 'Test Suites', path: 'test-suites' },
          { label: originSuiteName || 'Suite', path: 'suite' },
          { label: testCase?.name || `#${testCaseId}` },
        ]
      : [
          { label: 'Test Cases', path: 'test-cases' },
          { label: testCase?.name || `#${testCaseId}` },
        ];

    return <Breadcrumbs items={items} onNavigate={handleCrumbNavigate} />;
  }, [cameFromSuite, originSuiteName, testCase?.name, testCaseId, handleCrumbNavigate]);

  const backLabel = cameFromSuite
    ? `Back to ${originSuiteName || 'Suite'}`
    : 'Back to Test Cases';

  if (loading) {
    return (
      <PageScaffold title="Test Case" breadcrumbs={crumbs} compact>
        <LoadingState label="Loading test case…" />
      </PageScaffold>
    );
  }

  if (error || !testCase) {
    return (
      <PageScaffold title="Test Case" breadcrumbs={crumbs} compact>
        <ErrorState title="Unable to open test case" description={error} onRetry={fetchCase} />
        <div style={{ marginTop: 12 }}>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={goBack}>
            {cameFromSuite ? '← Back to Suite' : '← Back to Test Cases'}
          </button>
        </div>
      </PageScaffold>
    );
  }

  return (
    <PageScaffold
      title={
        <span className="rt-page-header__title-row">
          <button
            type="button"
            className="rt-page-header__back"
            onClick={goBack}
            title={backLabel}
            aria-label={backLabel}
          >
            Back
          </button>
          <span>{testCase.name}</span>
        </span>
      }
      breadcrumbs={crumbs}
      compact
      actions={
        <>
          <StatusBadge status={testCase.status} />
          <PriorityBadge priority={testCase.priority} />
        </>
      }
    >
      <ViewTestCaseModal
        isOpen
        variant="page"
        testCase={testCase}
        onClose={goBack}
        onDelete={() => goBack()}
      />
    </PageScaffold>
  );
};

export default TestCaseDetailPage;
